import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AnalysisResult, Recommendation, SymptomAnalysisInput } from "@/types/naturopathy";
import { consultationService } from "@/services/consultationService";
import { useToast } from "@/hooks/use-toast";
import { 
  AlertTriangle, 
  Leaf, 
  Utensils, 
  Heart, 
  Pill, 
  Activity,
  Download,
  Clock,
  Shield,
  ExternalLink,
  UserPlus,
  Baby,
  AlertCircle,
  Users,
  CheckCircle,
  CheckCircle2,
  XCircle,
  Info,
  FileText,
  Lightbulb,
  ShieldAlert,
  User
} from 'lucide-react';

// Helper pour parser les sections de l'explication
interface ParsedExplanationSections {
  general: string | null;
  medications: { types: string | null; warnings: string[] } | null;
  allergies: { name: string; description: string | null; plantsToAvoid: string[] }[];
  agePrecautions: { title: string; description: string; warnings: string[]; forms: string | null } | null;
  genderNote: string | null;
  conclusion: string | null;
}

const parseExplanationSections = (explanations: string): ParsedExplanationSections => {
  const result: ParsedExplanationSections = {
    general: null,
    medications: null,
    allergies: [],
    agePrecautions: null,
    genderNote: null,
    conclusion: null
  };

  // Séparer par double retour à la ligne ou sections connues
  const lines = explanations.split('\n');
  let currentSection = 'general';
  let generalText = '';
  let medTypes = '';
  let medWarnings: string[] = [];
  let currentAllergy: { name: string; description: string | null; plantsToAvoid: string[] } | null = null;
  let ageTitle = '';
  let ageDescription = '';
  let ageWarnings: string[] = [];
  let ageForms = '';
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    
    if (trimmedLine.includes('💊 MÉDICAMENTS EN COURS') || trimmedLine.includes('MÉDICAMENTS EN COURS')) {
      currentSection = 'medications';
      continue;
    }
    
    if (trimmedLine.includes('🚨 ALLERGIES CONNUES') || trimmedLine.includes('ALLERGIES CONNUES')) {
      currentSection = 'allergies';
      continue;
    }
    
    if (trimmedLine.includes('⚠️ PRÉCAUTIONS LIÉES À L\'ÂGE') || trimmedLine.includes('PRÉCAUTIONS LIÉES À L\'ÂGE')) {
      currentSection = 'age';
      const match = trimmedLine.match(/\(([^)]+)\)/);
      if (match) {
        ageTitle = `Précautions - ${match[1]}`;
      } else {
        ageTitle = 'Précautions liées à l\'âge';
      }
      continue;
    }
    
    if (trimmedLine.includes('spécificités physiologiques féminines') || trimmedLine.includes('physiologiques féminines')) {
      currentSection = 'gender';
    }
    
    if (trimmedLine.includes('Toutes les recommandations sont basées sur des données scientifiques')) {
      currentSection = 'conclusion';
    }
    
    switch (currentSection) {
      case 'general':
        if (trimmedLine) {
          generalText += (generalText ? ' ' : '') + trimmedLine;
        }
        break;
        
      case 'medications':
        if (trimmedLine.startsWith('Types identifiés :')) {
          medTypes = trimmedLine.replace('Types identifiés :', '').replace('.', '').trim();
        } else if (trimmedLine.startsWith('⚠️') || trimmedLine.startsWith('•')) {
          medWarnings.push(trimmedLine.replace('⚠️', '').replace('•', '').trim());
        } else if (trimmedLine && !trimmedLine.includes('MÉDICAMENTS')) {
          medWarnings.push(trimmedLine);
        }
        break;
        
      case 'allergies':
        if (trimmedLine.startsWith('•') && trimmedLine.includes(':')) {
          if (currentAllergy) {
            result.allergies.push(currentAllergy);
          }
          const parts = trimmedLine.replace('•', '').split(':');
          currentAllergy = {
            name: parts[0].trim(),
            description: parts.slice(1).join(':').trim() || null,
            plantsToAvoid: []
          };
        } else if (trimmedLine.includes('Plantes à éviter :') && currentAllergy) {
          const plants = trimmedLine.replace('Plantes à éviter :', '').trim();
          currentAllergy.plantsToAvoid = plants.split(',').map(p => p.trim().replace('...', ''));
        }
        break;
        
      case 'age':
        if (trimmedLine.startsWith('•')) {
          ageWarnings.push(trimmedLine.replace('•', '').trim());
        } else if (trimmedLine.startsWith('Formes recommandées :')) {
          ageForms = trimmedLine.replace('Formes recommandées :', '').trim();
        } else if (trimmedLine && !trimmedLine.includes('PRÉCAUTIONS')) {
          ageDescription += (ageDescription ? ' ' : '') + trimmedLine;
        }
        break;
        
      case 'gender':
        result.genderNote = trimmedLine.replace('Les ', '').replace('.', '').trim();
        currentSection = 'general'; // Reset après capture
        break;
        
      case 'conclusion':
        result.conclusion = trimmedLine;
        break;
    }
  }
  
  // Finaliser
  if (currentAllergy) {
    result.allergies.push(currentAllergy);
  }
  
  result.general = generalText || null;
  
  if (medTypes || medWarnings.length > 0) {
    result.medications = { types: medTypes || null, warnings: medWarnings };
  }
  
  if (ageTitle) {
    result.agePrecautions = {
      title: ageTitle,
      description: ageDescription,
      warnings: ageWarnings,
      forms: ageForms || null
    };
  }
  
  return result;
};

interface AnalysisResultsProps {
  result: AnalysisResult;
  symptomsInput: string;
  patientAge?: number;
  onExportPDF?: () => void;
  onReset?: () => void;
}

const AnalysisResults: React.FC<AnalysisResultsProps> = ({ result, symptomsInput, patientAge, onExportPDF, onReset }) => {
  const { toast } = useToast();
  const [patients, setPatients] = useState<any[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<string>('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    const data = await consultationService.getPatients();
    setPatients(data);
  };

  const handleSaveToPatient = async () => {
    if (!selectedPatient) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un patient",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    const saveResult = await consultationService.saveConsultation(selectedPatient, result, symptomsInput);
    setIsSaving(false);

    if (saveResult.success) {
      toast({
        title: "Consultation enregistrée",
        description: "L'analyse a été ajoutée à la fiche du patient",
      });
      setIsDialogOpen(false);
      setSelectedPatient('');
    } else {
      toast({
        title: "Erreur",
        description: "Impossible d'enregistrer la consultation",
        variant: "destructive",
      });
    }
  };

  const getRecommendationIcon = (type: Recommendation['type'], category?: string) => {
    // Si une catégorie détaillée est fournie, utiliser l'icône correspondante
    if (category) {
      const categoryLower = category.toLowerCase();
      if (categoryLower.includes('aromathérapie')) return <Leaf className="w-5 h-5 text-purple-500" />;
      if (categoryLower.includes('gemmothérapie')) return <Leaf className="w-5 h-5 text-green-600" />;
      if (categoryLower.includes('argile')) return <Shield className="w-5 h-5 text-amber-600" />;
      if (categoryLower.includes('huile végétale')) return <Heart className="w-5 h-5 text-yellow-500" />;
      if (categoryLower.includes('hydrolat')) return <Activity className="w-5 h-5 text-cyan-500" />;
      if (categoryLower.includes('apithérapie')) return <Heart className="w-5 h-5 text-amber-500" />;
    }
    
    switch (type) {
      case 'nutrition': return <Utensils className="w-5 h-5" />;
      case 'phytotherapie': return <Leaf className="w-5 h-5" />;
      case 'hygiene_vie': return <Heart className="w-5 h-5" />;
      case 'complement': return <Pill className="w-5 h-5" />;
      case 'technique_bien_etre': return <Activity className="w-5 h-5" />;
      default: return <Leaf className="w-5 h-5" />;
    }
  };

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case 1: return 'bg-destructive text-destructive-foreground';
      case 2: return 'bg-secondary text-secondary-foreground';
      case 3: return 'bg-muted text-muted-foreground';
    }
  };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 1: return 'Priorité haute';
      case 2: return 'Priorité moyenne';
      case 3: return 'Priorité faible';
    }
  };

  // Trier les recommandations par priorité
  const sortedRecommendations = [...result.recommendations].sort((a, b) => a.priority - b.priority);

  // Vérifier si c'est un enfant
  const isChild = patientAge !== undefined && patientAge <= 16;
  
  // Compter les remèdes non adaptés
  const unsuitsableCount = sortedRecommendations.filter(r => r.notSuitableForChildren).length;

  // Informations sur la catégorie d'âge
  const ageGuidelines = result.ageLifeGuidelines;

  // Fonction pour obtenir l'icône de la catégorie
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'infant': return <Baby className="w-5 h-5 text-pink-500" />;
      case 'child': return <Users className="w-5 h-5 text-blue-500" />;
      case 'preteen': return <Users className="w-5 h-5 text-indigo-500" />;
      case 'pregnant': return <Heart className="w-5 h-5 text-rose-500" />;
      case 'breastfeeding': return <Heart className="w-5 h-5 text-pink-400" />;
      case 'elderly': return <Users className="w-5 h-5 text-amber-600" />;
      default: return <Users className="w-5 h-5" />;
    }
  };

  // Fonction pour obtenir la couleur de la catégorie
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'infant': return 'border-pink-500 bg-pink-50 dark:bg-pink-950/30';
      case 'child': return 'border-blue-500 bg-blue-50 dark:bg-blue-950/30';
      case 'preteen': return 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30';
      case 'pregnant': return 'border-rose-500 bg-rose-50 dark:bg-rose-950/30';
      case 'breastfeeding': return 'border-pink-400 bg-pink-50 dark:bg-pink-950/30';
      case 'elderly': return 'border-amber-500 bg-amber-50 dark:bg-amber-950/30';
      default: return 'border-gray-300 bg-gray-50';
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6">
      {/* Section Précautions liées à l'âge/état physiologique */}
      {ageGuidelines && (
        <Card className={`shadow-card border-2 ${getCategoryColor(ageGuidelines.category)}`}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-3 text-lg">
              {getCategoryIcon(ageGuidelines.category)}
              <span>Précautions - {ageGuidelines.label} ({ageGuidelines.ageRange})</span>
            </CardTitle>
            <CardDescription className="text-foreground/80 font-medium">
              {ageGuidelines.description}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Avertissements importants */}
            {ageGuidelines.warnings.length > 0 && (
              <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                <h4 className="font-semibold text-sm text-destructive flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  Avertissements importants
                </h4>
                <ul className="text-sm space-y-1 text-destructive/90">
                  {ageGuidelines.warnings.map((warning, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="mt-1">•</span>
                      <span>{warning}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Formes autorisées */}
              {ageGuidelines.allowedForms.length > 0 && (
                <div className="p-3 bg-green-50 dark:bg-green-950/30 border border-green-300 rounded-lg">
                  <h4 className="font-semibold text-sm text-green-700 dark:text-green-400 flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4" />
                    Formes galéniques autorisées
                  </h4>
                  <ul className="text-sm space-y-1 text-green-700 dark:text-green-300">
                    {ageGuidelines.allowedForms.map((form, idx) => (
                      <li key={idx}>✓ {form}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Formes interdites */}
              {ageGuidelines.forbiddenForms.length > 0 && (
                <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-300 rounded-lg">
                  <h4 className="font-semibold text-sm text-red-700 dark:text-red-400 flex items-center gap-2 mb-2">
                    <XCircle className="w-4 h-4" />
                    Formes à éviter
                  </h4>
                  <ul className="text-sm space-y-1 text-red-700 dark:text-red-300">
                    {ageGuidelines.forbiddenForms.map((form, idx) => (
                      <li key={idx}>✗ {form}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Plantes sûres */}
              {ageGuidelines.safePlants.length > 0 && (
                <div className="p-3 bg-green-50 dark:bg-green-950/30 border border-green-300 rounded-lg">
                  <h4 className="font-semibold text-sm text-green-700 dark:text-green-400 flex items-center gap-2 mb-2">
                    <Leaf className="w-4 h-4" />
                    Plantes généralement bien tolérées
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {ageGuidelines.safePlants.map((plant, idx) => (
                      <Badge key={idx} variant="outline" className="bg-green-100 text-green-700 border-green-300 text-xs">
                        {plant}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Plantes à éviter */}
              {ageGuidelines.plantsToAvoid.length > 0 && (
                <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-300 rounded-lg">
                  <h4 className="font-semibold text-sm text-red-700 dark:text-red-400 flex items-center gap-2 mb-2">
                    <AlertCircle className="w-4 h-4" />
                    Plantes à éviter
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {ageGuidelines.plantsToAvoid.map((plant, idx) => (
                      <Badge key={idx} variant="outline" className="bg-red-100 text-red-700 border-red-300 text-xs">
                        {plant}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Notes de dosage */}
            {ageGuidelines.dosageNotes.length > 0 && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-300 rounded-lg">
                <h4 className="font-semibold text-sm text-blue-700 dark:text-blue-400 flex items-center gap-2 mb-2">
                  <Info className="w-4 h-4" />
                  Notes de dosage
                </h4>
                <ul className="text-sm space-y-1 text-blue-700 dark:text-blue-300">
                  {ageGuidelines.dosageNotes.map((note, idx) => (
                    <li key={idx}>📋 {note}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Alerte si remèdes non adaptés */}
      {unsuitsableCount > 0 && (
        <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-950/30">
          <Baby className="h-5 w-5 text-amber-600" />
          <AlertTitle className="text-amber-700 dark:text-amber-400 font-semibold flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {ageGuidelines ? `${ageGuidelines.label}` : isChild ? `Consultant enfant (${patientAge} ans)` : ''} - Attention particulière requise
          </AlertTitle>
          <AlertDescription className="mt-2 text-amber-700 dark:text-amber-300">
            <strong>{unsuitsableCount} remède(s)</strong> dans cette analyse nécessitent une attention particulière. 
            Ces remèdes sont signalés par un bandeau orange <Badge variant="outline" className="ml-1 mr-1 bg-amber-100 text-amber-700 border-amber-300 text-xs">Non adapté</Badge>.
            <br />
            Veuillez privilégier les alternatives adaptées ou consulter un professionnel de santé.
          </AlertDescription>
        </Alert>
      )}
      {/* En-tête avec timestamp et actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold text-primary">Analyse Naturopathique</h2>
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {new Date(result.timestamp).toLocaleString('fr-FR')}
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="default" size="sm">
                <UserPlus className="w-4 h-4 mr-2" />
                Lier à un patient
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Lier cette analyse à un patient</DialogTitle>
                <DialogDescription>
                  Sélectionnez le patient pour enregistrer cette analyse dans sa fiche.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <Select value={selectedPatient} onValueChange={setSelectedPatient}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map((patient) => (
                      <SelectItem key={patient.id} value={patient.id}>
                        {patient.name} {patient.email && `(${patient.email})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button onClick={handleSaveToPatient} disabled={isSaving}>
                    {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Button variant="outline" size="sm" onClick={onExportPDF}>
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <Button variant="outline" size="sm" onClick={onReset}>
            Nouvelle analyse
          </Button>
        </div>
      </div>

      {/* Triage et alertes */}
      {result.triage.level !== 'none' && (
        <Alert className={`${result.triage.level === 'urgent_medical' ? 'border-destructive bg-destructive-light' : 'border-secondary bg-secondary-light'}`}>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle className="font-semibold">
            {result.triage.level === 'urgent_medical' ? 'Consultation médicale urgente requise' : 'Attention'}
          </AlertTitle>
          <AlertDescription className="mt-2">
            {result.triage.message}
            {result.triage.redFlags.length > 0 && (
              <div className="mt-2">
                <strong>Signaux d'alarme détectés :</strong>
                <ul className="list-disc list-inside mt-1">
                  {result.triage.redFlags.map((flag, index) => (
                    <li key={index}>{flag}</li>
                  ))}
                </ul>
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Alertes d'interactions */}
      {result.interactions_alert.length > 0 && (
        <Alert className="border-destructive bg-destructive-light">
          <Shield className="h-4 w-4" />
          <AlertTitle>Interactions médicamenteuses détectées</AlertTitle>
          <AlertDescription className="mt-2 space-y-2">
            {result.interactions_alert.map((interaction, index) => (
              <div key={index} className="p-2 bg-background/50 rounded">
                <div className="flex items-center gap-2 font-medium">
                  <Badge variant={interaction.severity === 'major' ? 'destructive' : interaction.severity === 'moderate' ? 'secondary' : 'outline'}>
                    {interaction.severity === 'major' ? 'Majeure' : interaction.severity === 'moderate' ? 'Modérée' : 'Mineure'}
                  </Badge>
                  {interaction.medication} × {interaction.plant}
                </div>
                <p className="text-sm mt-1">{interaction.description}</p>
              </div>
            ))}
          </AlertDescription>
        </Alert>
      )}

      {/* Recommandations principales */}
      {result.triage.level !== 'urgent_medical' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sortedRecommendations.map((recommendation, index) => (
            <Card 
              key={index} 
              className={`shadow-card bg-gradient-card relative ${
                recommendation.notSuitableForChildren 
                  ? 'border-amber-400 border-2' 
                  : ''
              }`}
            >
              {/* Badge "Non adapté enfant" en haut de la carte */}
              {recommendation.notSuitableForChildren && (
                <div className="absolute -top-3 left-4 z-10">
                  <Badge 
                    variant="outline" 
                    className="bg-amber-100 text-amber-700 border-amber-400 font-semibold shadow-sm flex items-center gap-1"
                  >
                    <Baby className="w-3 h-3" />
                    Non adapté enfant {recommendation.minimumAge && `(< ${recommendation.minimumAge} ans)`}
                  </Badge>
                </div>
              )}
              
              <CardHeader className={`pb-4 ${recommendation.notSuitableForChildren ? 'pt-6' : ''}`}>
                <div className="flex items-start justify-between">
                  <CardTitle className="flex items-center gap-3 text-lg">
                    {getRecommendationIcon(recommendation.type, recommendation.category)}
                    {recommendation.title}
                  </CardTitle>
                  <Badge className={getPriorityColor(recommendation.priority)}>
                    {getPriorityLabel(recommendation.priority)}
                  </Badge>
                </div>
                {recommendation.confidence && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all" 
                        style={{ width: `${recommendation.confidence * 100}%` }}
                      />
                    </div>
                    <span>{Math.round(recommendation.confidence * 100)}% confiance</span>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Alerte visuelle pour enfant */}
                {recommendation.notSuitableForChildren && recommendation.childrenWarning && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-300 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-sm text-amber-700 dark:text-amber-400">Attention - Consultant enfant</h4>
                      <p className="text-sm text-amber-600 dark:text-amber-300">{recommendation.childrenWarning}</p>
                    </div>
                  </div>
                )}
                
                <p className="text-foreground">{recommendation.detail}</p>
                
                {recommendation.posology && (
                  <div className="p-3 bg-primary-light rounded-lg">
                    <h4 className="font-medium text-sm text-primary mb-1">Posologie suggérée</h4>
                    <p className="text-sm text-primary">{recommendation.posology}</p>
                  </div>
                )}
                
                {recommendation.contraindications.length > 0 && (
                  <div className="p-3 bg-destructive-light rounded-lg">
                    <h4 className="font-medium text-sm text-destructive mb-2">Contre-indications</h4>
                    <ul className="text-sm text-destructive space-y-1">
                      {recommendation.contraindications.map((ci, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-destructive">•</span>
                          {ci}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Explications et justifications - Structurées */}
      <Card className="shadow-card bg-gradient-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Justification de l'approche
          </CardTitle>
          <CardDescription>
            Analyse détaillée et explications des recommandations proposées
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Parser et afficher les sections de manière structurée */}
          {(() => {
            const sections = parseExplanationSections(result.explanations);
            return (
              <div className="space-y-4">
                {/* Section d'approche générale */}
                {sections.general && (
                  <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-primary/10 rounded-full shrink-0">
                        <Lightbulb className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium text-sm text-foreground mb-1">Approche naturopathique</h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">{sections.general}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Section médicaments */}
                {sections.medications && (
                  <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-amber-500/20 rounded-full shrink-0">
                        <Pill className="h-4 w-4 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-sm text-amber-700 dark:text-amber-400 mb-2">Médicaments en cours</h4>
                        <div className="space-y-2">
                          {sections.medications.types && (
                            <div className="flex flex-wrap gap-2">
                              {sections.medications.types.split(', ').map((type, idx) => (
                                <Badge key={idx} variant="outline" className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30">
                                  {type}
                                </Badge>
                              ))}
                            </div>
                          )}
                          {sections.medications.warnings.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {sections.medications.warnings.map((warning, idx) => (
                                <div key={idx} className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300">
                                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                                  <span>{warning}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Section allergies */}
                {sections.allergies && sections.allergies.length > 0 && (
                  <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-red-500/20 rounded-full shrink-0">
                        <ShieldAlert className="h-4 w-4 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-sm text-red-700 dark:text-red-400 mb-2">Allergies connues</h4>
                        <div className="grid gap-3">
                          {sections.allergies.map((allergy, idx) => (
                            <div key={idx} className="bg-red-500/5 rounded-md p-3">
                              <div className="font-medium text-sm text-red-700 dark:text-red-400">{allergy.name}</div>
                              {allergy.description && (
                                <p className="text-sm text-red-600/80 dark:text-red-300/80 mt-1">{allergy.description}</p>
                              )}
                              {allergy.plantsToAvoid && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {allergy.plantsToAvoid.map((plant, pIdx) => (
                                    <Badge key={pIdx} variant="outline" className="text-xs bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30">
                                      {plant}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Section âge et précautions */}
                {sections.agePrecautions && (
                  <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-500/20 rounded-full shrink-0">
                        <User className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-sm text-blue-700 dark:text-blue-400 mb-2">{sections.agePrecautions.title}</h4>
                        <p className="text-sm text-blue-600/80 dark:text-blue-300/80">{sections.agePrecautions.description}</p>
                        {sections.agePrecautions.warnings.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {sections.agePrecautions.warnings.map((warning, idx) => (
                              <li key={idx} className="text-sm text-blue-600 dark:text-blue-300 flex items-start gap-2">
                                <span className="text-blue-500">•</span>
                                <span>{warning}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {sections.agePrecautions.forms && (
                          <div className="mt-3 p-2 bg-blue-500/10 rounded">
                            <span className="text-xs font-medium text-blue-700 dark:text-blue-400">Formes recommandées : </span>
                            <span className="text-xs text-blue-600 dark:text-blue-300">{sections.agePrecautions.forms}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Section genre */}
                {sections.genderNote && (
                  <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-purple-500/20 rounded-full shrink-0">
                        <Heart className="h-4 w-4 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-sm text-purple-700 dark:text-purple-400 mb-1">Spécificités physiologiques</h4>
                        <p className="text-sm text-purple-600/80 dark:text-purple-300/80">{sections.genderNote}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Note de conclusion */}
                {sections.conclusion && (
                  <div className="p-3 bg-muted/50 rounded-lg border-l-4 border-primary">
                    <p className="text-sm text-muted-foreground italic">{sections.conclusion}</p>
                  </div>
                )}
              </div>
            );
          })()}
          
          {/* Score de confiance */}
          <div className="mt-6 pt-4 border-t border-border/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-full">
                <CheckCircle2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">Score de confiance global</span>
                  <span className="text-sm font-bold text-primary">
                    {Math.round(result.confidence_score * 100)}%
                  </span>
                </div>
                <div className="bg-muted rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-primary/60 to-primary h-2 rounded-full transition-all duration-500" 
                    style={{ width: `${result.confidence_score * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sources et références */}
      {result.sources.length > 0 && (
        <Card className="shadow-card bg-gradient-card">
          <CardHeader>
            <CardTitle className="text-lg">Sources et références</CardTitle>
            <CardDescription>
              Bases scientifiques des recommandations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {result.sources.map((source, index) => (
              <div key={index} className="p-4 border border-border rounded-lg space-y-2">
                <div className="flex items-start justify-between">
                  <h4 className="font-medium text-foreground">{source.title}</h4>
                  <Badge variant={
                    source.reliability === 'high' ? 'default' : 
                    source.reliability === 'medium' ? 'secondary' : 'outline'
                  }>
                    Fiabilité {source.reliability === 'high' ? 'élevée' : source.reliability === 'medium' ? 'moyenne' : 'faible'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{source.summary}</p>
                {source.url && (
                  <a 
                    href={source.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-hover"
                  >
                    Voir la source <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Disclaimer légal */}
      <div className="text-xs text-muted-foreground bg-muted/50 p-4 rounded-lg">
        <strong>Avertissement légal :</strong> Ces conseils naturopathiques sont fournis à titre informatif et complémentaire. 
        Ils ne remplacent en aucun cas un diagnostic médical ou un traitement prescrit par un professionnel de santé. 
        En cas de doute ou de symptômes persistants, consultez votre médecin traitant ou un spécialiste.
      </div>
    </div>
  );
};

export default AnalysisResults;